"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, SearchCheck, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Mail, Phone, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: 1,
  title: "Direct Access",
  desc: "You work directly with me—no account managers or junior pass-offs."
}, {
  id: 2,
  title: "Actionable Roadmap",
  desc: "A clear technical, content, and search visibility strategy tailored to your revenue goals."
}, {
  id: 3,
  title: "No Lock-in Pressure",
  desc: "Transparent discussions, clear commercial metrics, and honest feasibility feedback."
}];
export default function Conversion() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="conversioncta" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground shadow-2xl px-6 py-12 sm:px-12 sm:py-16 lg:p-20">
          {/* Subtle Decorative Elements */}
          <div className="pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-accent/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-secondary/15 blur-3xl" />

          <div className="relative z-10 max-w-4xl mx-auto text-center space-y-8">
            <motion.div variants={itemVariants} className="flex justify-center">
              <Badge variant="outline" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 px-3.5 py-1 text-xs font-semibold tracking-wide uppercase font-serif">
                <Sparkles className="size-3.5 mr-1.5 text-accent" />
                Direct Consultant Partnership
              </Badge>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-primary-foreground leading-tight">
                Ready to Grow Your Organic Traffic and Revenue?
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-primary-foreground/90 max-w-2xl mx-auto leading-relaxed">
                When you hire me, you get my direct personal expertise on every
                audit, technical fix, and growth sprint. Let us explore how we
                can sustainably scale your search visibility.
              </p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div variants={itemVariants} className="flex flex-col items-center justify-center gap-3 pt-2">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
                <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg font-serif font-semibold h-12 px-7 rounded-xl transition-[color,background-color,box-shadow]">
                  <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                    <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                    <span>Book a Free SEO Consultation</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-primary-foreground/10 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/20 hover:text-primary-foreground shadow-md font-serif font-medium h-12 px-7 rounded-xl transition-[color,background-color,border-color]">
                  <Link to="/SeoAuditsPage" className="inline-flex items-center justify-center gap-2">
                    <SearchCheck className="size-4" />
                    <span>Get a Free SEO Audit</span>
                  </Link>
                </Button>
              </div>

              <p className="text-xs sm:text-sm text-primary-foreground/80 max-w-xl text-center leading-relaxed pt-1">
                No sales pitch, no commitment, no inflated promises — just a clear, honest assessment of where your search visibility stands.
              </p>
            </motion.div>

            {/* Value Props Checklist */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 text-left border-t border-primary-foreground/15">
              {benefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="rounded-xl bg-primary-foreground/5 p-4 border border-primary-foreground/10 backdrop-blur-xs space-y-1.5">
                  <div className="flex items-center gap-2 text-accent font-serif font-semibold text-sm">
                    <CheckCircle2 className="size-4 shrink-0" />
                    <span>{benefit.title}</span>
                  </div>
                  <p className="text-xs text-primary-foreground/80 leading-relaxed pl-6">
                    {benefit.desc}
                  </p>
                </div>)}
            </motion.div>

            {/* Quick Contact Line */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center gap-6 pt-4 text-xs sm:text-sm text-primary-foreground/80">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-accent" />
                <span>Zero obligations. Honest recommendations only.</span>
              </div>
              <span className="hidden sm:inline text-primary-foreground/30">•</span>
              <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-1.5 hover:text-primary-foreground transition-colors">
                <Mail className="size-3.5 text-accent" />
                <span>contact@ye-consultancy.com</span>
              </a>
              <span className="hidden sm:inline text-primary-foreground/30">•</span>
              <a href="tel:+447490984430" className="inline-flex items-center gap-1.5 hover:text-primary-foreground transition-colors">
                <Phone className="size-3.5 text-accent" />
                <span>+44 7490984430</span>
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}